import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Box, Fade, Stack, OutlinedInput, InputAdornment, Button } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DisplayMetrcTagsTable from '../../utility/tables/DisplayMetrcTagsTable';
import { roomDetailClasses, tableClasses, errFeedbackFn } from '../../utility';
import apiService from '../../../api/apiService';
import { FeedbackContext } from '../../../App';
import { useToken } from '../../../utility';
import { failsTokenAndSetupCheck } from '../../../checks/setup';
import { MetrcLogo } from '../../utility';

export default function _LabTestTypesTab(props) {
  const { currDarkThemeClass = '', ...otherProps } = props;

  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [syncLoading, setSyncLoading] = useState(false);
  const feedbackCX = useContext(FeedbackContext);
  const { token, tokenDetails } = useToken();
  const invalidTokenState = useMemo(() => failsTokenAndSetupCheck(token, tokenDetails), [token, tokenDetails]);

  // Debounce effect for search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPage(0); 
    }, 300);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  const fetchTypeData = async (currentPage = page, limit = rowsPerPage, search = debouncedSearchTerm) => {
    setLoading(true);
    try {
      let url = `labtest-types/?limit=${limit}&page=${currentPage + 1}`;
      if (search) {
        url += `&name__icontains=${encodeURIComponent(search)}`;
      }
      const response = await apiService.fetchInstance(url);
      if (response?.data) {
        const formattedData = formatData(response.data.results || []);
        setData(formattedData);
        setTotalCount(response.data.count || 0);
      }
    } catch (error) {
      feedbackCX.setContextValue(true, errFeedbackFn(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (invalidTokenState) return;
    fetchTypeData(page, rowsPerPage, debouncedSearchTerm);
    // eslint-disable-next-line
  }, [page, rowsPerPage, debouncedSearchTerm, invalidTokenState]);

  const formatData = formattedData => {
    return formattedData.map((type, idx) => [
      `${idx + 1 + page * rowsPerPage}`.padStart(2, '0'),
      type.name || '',
      type.lab_test_result_mode || '',
      type.lab_test_result_minimum ?? '',
      type.lab_test_result_maximum ?? '',
      type.requires_test_result ? 'Yes' : 'No',
      type.informational_only ? 'Yes' : 'No',
      type.always_passes ? 'Yes' : 'No',
    ]);
  };

  const typeKeyTitleMap = [
    { key: 'name', title: 'Name' },
    { key: 'lab_test_result_mode', title: 'Result Mode' },
    { key: 'lab_test_result_minimum', title: 'Min Value' },
    { key: 'lab_test_result_maximum', title: 'Max Value' },
    { key: 'requires_test_result', title: 'Requires Result' },
    { key: 'informational_only', title: 'Informational Only' },
    { key: 'always_passes', title: 'Always Passes' },
  ];

  const tableTitles = typeKeyTitleMap.map(e => ({ title: e.title }));

  const handlePageChange = (newPage, newRowsPerPage) => {
    setPage(newPage);
    setRowsPerPage(newRowsPerPage);
  };

  // Simplified handleSearch
  const handleSearch = event => {
    setSearchTerm(event.target.value);
  };

  const handleSyncToMetrc = async () => {
    setSyncLoading(true);
    let syncUrl = `labtest-types/?sync_to_metrc=true`;
    try {
      const response = await apiService.fetchInstance(syncUrl);
      if (response.status === 200) {
        fetchTypeData();
        feedbackCX.setContextValue(true, {
          type: 'success',
          message: 'Lab Test Types Synced From Metrc Successfully.',
        });
        setTimeout(() => {
          feedbackCX.setContextValue(false, {});
        }, 5000);
      }
    } catch (error) {
      feedbackCX.setContextValue(true, {
        type: 'error',
        message: `Error syncing lab test types from Metrc`,
      });
    } finally {
      setSyncLoading(false);
    }
  };

  const outlinedInputProps = {
    className: `${roomDetailClasses.filterInput} ${currDarkThemeClass}`,
    size: 'small',
    placeholder: 'Search using Type Name',
    startAdornment: (
      <InputAdornment position="start">
        <SearchIcon />
      </InputAdornment>
    ),
    onChange: handleSearch,
    value: searchTerm,
  };

  const typeTableValues = {
    animationClass: 'farmsTabContent',
    currDarkThemeClass,
    emptyTableMsg: 'No Lab Test Types Found',
    entity: 'Lab Test Types',
    tableEntries: data,
    tableTitles: [{ title: 'Sl. No' }, ...tableTitles],
    toShowAddBtn: false,
    page,
    rowsPerPage,
    totalCount,
    onPageChange: handlePageChange,
    loading,
    ...otherProps,
  };

  return (
    <Fade in={true} timeout={2000}>
      <Box>
        <Stack direction="row" justifyContent="flex-end" padding={2}>
          <Button
            onClick={handleSyncToMetrc}
            disabled={syncLoading}
            style={{ display: 'flex', alignItems: 'center', paddingRight: '20px' }}
            sx={{ color: '#B1D23A', '&.Mui-disabled': { color: '#B1D23A' } }}
          >
            <MetrcLogo style={{ width: 24, height: 24, marginRight: 8 }} />
            {syncLoading ? 'Syncing...' : 'Sync From Metrc'}
          </Button>
          <OutlinedInput {...outlinedInputProps} />
        </Stack>
        <Box className={`${tableClasses?.container} ${currDarkThemeClass}`}>
          <DisplayMetrcTagsTable {...typeTableValues} />
        </Box>
      </Box>
    </Fade>
  );
}